import React, { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import classnames from "classnames";
import MenuNav from "./MenuNav";
import { MenuItem, MENUS } from "../../../constants";
import {
  getActiveOrganization,
  getCurrentUser,
} from "../../../redux/selectors";
import { visibleMenuTree } from "../../../utils/helpers";

interface IFullLayoutSidebarProps {
  sidebarCollapsed: boolean;
  onToggleSidebar(): void;
  navCollapsed: boolean;
  onToggleNav(): void;
}

const FullLayoutSidebar: FC<IFullLayoutSidebarProps> = ({
  sidebarCollapsed,
  onToggleSidebar,
  navCollapsed,
  onToggleNav,
}) => {
  const location = useLocation();
  const [menus, setMenus] = useState<MenuItem[]>(MENUS);
  const [activeMenuText, setActiveMenuText] = useState<string>();
  const organization = useSelector(getActiveOrganization);
  const currentUser = useSelector(getCurrentUser);

  useEffect(() => {
    // let newMenus = MENUS;
    // MENUS.forEach((menu) => {
    //   (menu?.subMenus || []).forEach((item) => {
    //     if (item.pinned && item.pin === 'standard') {
    //       newMenus = pinMenuItem(newMenus, menu, item);
    //     }
    //   });
    // });
    // setMenus(newMenus);

    let menu = MENUS.find(
      (item) => item.prefix && location.pathname.startsWith(item.prefix)
    );
    if (!menu) {
      menu = MENUS.find(
        (item) => item.link && location.pathname.startsWith(item.link)
      );
    }
    setActiveMenuText(menu?.text || MENUS[0].text);
  }, [location]);

  // The same gates at every level. Settings and WhatsApp carry their flags on
  // their SUB-menus, which is exactly the level that used to be drawn ungated.
  // Pruned at every level. `MenuNav` renders each item's own `subMenus`, so the
  // filtered copy this used to build for `activeMenu` alone never reached the
  // screen -- see `visibleMenuTree`.
  const visibleMenus = useMemo(
    () => visibleMenuTree(menus, organization, currentUser),
    [menus, organization, currentUser],
  );

  // Off the pruned tree, so it cannot disagree with what `MenuNav` renders.
  const activeMenu = useMemo(
    () =>
      visibleMenus.find(
        (item) => item.type === "menu" && item.text === activeMenuText,
      ),
    [activeMenuText, visibleMenus],
  );

  const pinMenuItem = (menus: MenuItem[], menu: MenuItem, item: MenuItem) => {
    if (item.pin === "toggle" && item.pinned) {
      return menus;
    }

    const id = menus.findIndex((m) => m.text === menu.text);
    const newMenus = [...menus];
    const newMenu = { ...menu };
    newMenus[id] = newMenu;
    if (menu.pin === "standard") {
      const newItem = { ...item, pinned: true };
      newMenu.subMenus = (newMenu.subMenus as MenuItem[]).map((subMenu) =>
        subMenu.text === item.text ? newItem : subMenu
      );
      newMenus.splice(id, 0, newItem);
    } else {
      newMenu.link = item.link;
      newMenu.subMenus = (newMenu.subMenus as MenuItem[]).map((subMenu) => ({
        ...subMenu,
        pinned: subMenu.text === item.text,
      }));
    }
    return newMenus;
  };

  const unpinMenuItem = (menus: MenuItem[], menu: MenuItem, item: MenuItem) => {
    if (item.pin === "toggle") {
      return menus;
    }

    const newItem = { ...item, pinned: false };
    const newMenu = {
      ...menu,
      subMenus: (menu.subMenus as MenuItem[]).map((subMenu) =>
        subMenu.text === item.text ? newItem : subMenu
      ),
    };
    return menus
      .filter((m) => m.text !== item.text)
      .map((m) => (m.text === menu.text ? newMenu : m));
  };

  const onTogglePin = (item: MenuItem) => {
    if (item.pinned) {
      const newMenus = unpinMenuItem(menus, activeMenu as MenuItem, item);
      setMenus(newMenus);
    } else {
      const newMenus = pinMenuItem(menus, activeMenu as MenuItem, item);
      setMenus(newMenus);
    }
  };

  return (
    <div
      className={classnames(
        "sidebar h-full flex overflow-visible z-[1000] lg:z-20 shadow-lg absolute lg:relative"
      )}
    >
      <MenuNav
        sidebarCollapsed={sidebarCollapsed}
        navCollapsed={navCollapsed}
        onToggleNav={onToggleNav}
        menus={visibleMenus}
        activeMenu={activeMenu}
        onSelectMenu={(menu) => setActiveMenuText(menu?.text)}
        onTogglePin={onTogglePin}
        onToggleSidebar={onToggleSidebar}
      />
    </div>
  );
};

export default FullLayoutSidebar;
